/**
 * What the panel shows for a PDF split job of a split major job - pure, so it
 * is tested without a browser. A split job speaks the words task jobs speak:
 * the same status chip (PENDING / PROCESSING / FINISHED / ERROR / STOPPED), the
 * same tags, the same action row.
 */
import type { MetaTone } from "../MetaPill";
import type {
  PdfActiveJobRef,
  PdfJobBase,
  PdfJobPublishShort,
  PdfJobStatus,
  PdfSplitterInfo,
  PdfToolsConfig,
  SplitSsnCheck,
  SplitSsnCheckSummary,
} from "features/pdfTools/pdfTools";
import { statusChipColor, type ChipColor } from "utils/botStatusColors";

export const SPLIT_STATUS_WORDS: Record<PdfJobStatus, string> = {
  pending: "PENDING",
  running: "PROCESSING",
  completed: "FINISHED",
  failed: "ERROR",
  stopped: "STOPPED",
  canceled: "STOPPED",
};

export function splitStatusChip(status: PdfJobStatus): {
  label: string;
  color: ChipColor;
} {
  const label = SPLIT_STATUS_WORDS[status] ?? String(status || "").toUpperCase();
  return { label, color: statusChipColor(label) };
}

export const SPLIT_PHASE_LABELS: Record<string, string> = {
  downloading: "Downloading files",
  extracting: "Extracting PDFs",
  splitting: "Splitting and naming",
  uploading: "Uploading results",
  zipping: "Building the archive",
};

type SplitJobState = Pick<PdfJobBase, "status"> & {
  publish: Pick<PdfJobPublishShort, "status">;
  discarded_at?: string | null;
};

export function isSplitJobActive(job: Pick<PdfJobBase, "status">): boolean {
  return job.status === "pending" || job.status === "running";
}

export function isSplitJobSaving(job: { publish: Pick<PdfJobPublishShort, "status"> }): boolean {
  return job.publish.status === "pending" || job.publish.status === "running";
}

export function isSplitJobSaved(job: SplitJobState): boolean {
  return !job.discarded_at && job.publish.status === "completed";
}

/** The result of the split, next to the status: null when there is none. */
export function splitResultTag(
  job: SplitJobState,
): { label: string; tone: MetaTone } | null {
  if (job.discarded_at) return null;
  if (job.publish.status === "completed") return { label: "Saved", tone: "success" };
  if (isSplitJobSaving(job)) return { label: "Saving", tone: "info" };
  if (job.publish.status === "failed") return { label: "Save failed", tone: "error" };
  if (job.status === "completed") return { label: "In review", tone: "info" };
  return null;
}

type SsnCheckLike = Pick<SplitSsnCheck, "status"> & {
  ssn_updater?: { state?: string | null; status?: string } | null;
};

function ssnCheckBusy(check: SsnCheckLike | null | undefined): boolean {
  if (!check) return false;
  const updater = check.ssn_updater;
  const updaterRunning =
    updater != null && (updater.state ?? updater.status) === "running";
  return check.status === "running" || updaterRunning;
}

/** Whether a list of split jobs has anything moving that only a refresh shows. */
export function splitListNeedsPolling(
  rows: (SplitJobState & { ssn_check?: SplitSsnCheckSummary | null })[],
): boolean {
  return rows.some(
    (row) => isSplitJobActive(row) || isSplitJobSaving(row) || ssnCheckBusy(row.ssn_check),
  );
}

/** The SSN check starts as the save ends; a minute of grace keeps the page
 * asking until it has an answer. */
export const SSN_CHECK_GRACE_MS = 60_000;

export function splitJobNeedsPolling(
  job: SplitJobState & {
    ssn_check?: SplitSsnCheck | null;
    publish: Pick<PdfJobPublishShort, "status" | "published_at">;
  },
  now: number = Date.now(),
): boolean {
  if (isSplitJobActive(job) || isSplitJobSaving(job) || ssnCheckBusy(job.ssn_check)) {
    return true;
  }
  if (isSplitJobSaved(job) && !job.ssn_check && job.publish.published_at) {
    const savedAt = new Date(job.publish.published_at).getTime();
    return !Number.isNaN(savedAt) && now - savedAt < SSN_CHECK_GRACE_MS;
  }
  return false;
}

/** A moment in the viewer's locale; "-" when there is none. `short` drops the
 * seconds and the century, for a table cell. */
export function formatSplitDateTime(
  value: string | null | undefined,
  { short = false }: { short?: boolean } = {},
): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return short
    ? date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
    : date.toLocaleString();
}

function humanizeOptionKey(key: string): string {
  const words = key.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** The job's options as label/value lines, titled by the splitter's form. Only
 * what the form asks for shows (a splitter stores hidden defaults too), and a
 * secret field never does, whatever the stored options hold. Nothing shows
 * until the form is known, so no line appears only to vanish. */
export function splitOptionLines(
  options: Record<string, unknown> | null | undefined,
  splitter: Pick<PdfSplitterInfo, "options_schema"> | null | undefined,
): { label: string; value: string }[] {
  const properties = splitter?.options_schema?.properties;
  if (!properties) return [];
  return Object.entries(options ?? {}).flatMap(([key, value]) => {
    if (!(key in properties)) return [];
    const property = properties[key];
    if (property?.writeOnly || property?.format === "password") return [];
    if (value === null || value === undefined || value === "") return [];
    if (!["string", "number", "boolean"].includes(typeof value)) return [];
    return [
      {
        label: property.title || humanizeOptionKey(key),
        value: typeof value === "boolean" ? (value ? "on" : "off") : String(value),
      },
    ];
  });
}

function count(value: number | undefined | null): string {
  return Number(value ?? 0).toLocaleString();
}

/** The progress cell: a line, a second line and the bar. */
export function splitProgressSummary(job: Pick<PdfJobBase, "status" | "progress" | "totals">): {
  primary: string;
  secondary: string;
  percent: number | null;
} {
  const { progress, totals } = job;
  if (isSplitJobActive(job)) {
    const phase = SPLIT_PHASE_LABELS[progress.phase] ?? (job.status === "pending" ? "Waiting to start" : "Working");
    if (progress.total_pages > 0) {
      return {
        primary: `${count(progress.processed_pages)} / ${count(progress.total_pages)} pages`,
        secondary: phase,
        percent: Math.min(100, (progress.processed_pages / progress.total_pages) * 100),
      };
    }
    return { primary: phase, secondary: "", percent: null };
  }
  if (job.status === "completed") {
    // What an operator counts first; the pages and the misses under it.
    const errorPages = Number(totals.error_pages ?? 0);
    const pages = `${count(totals.pages)} pages`;
    return {
      primary: `${count(totals.employee_folders)} employees`,
      secondary: errorPages > 0 ? `${pages}, ${count(errorPages)} error page(s)` : pages,
      percent: 100,
    };
  }
  // A stopped or failed split shows how far it got.
  const reached =
    progress.total_pages > 0
      ? {
          primary: `${count(progress.processed_pages)} / ${count(progress.total_pages)} pages`,
          percent: Math.min(100, (progress.processed_pages / progress.total_pages) * 100),
        }
      : { primary: "-", percent: null };
  return {
    ...reached,
    secondary: job.status === "stopped" || job.status === "canceled" ? "Stopped" : "Failed",
  };
}

/** "W2_2025.zip +2 more". */
export function formatSplitInputs(names: string[], total: number): string {
  const first = names.find(Boolean);
  if (!first) return total > 0 ? `${total} file(s)` : "";
  const more = Math.max(total, names.length) - 1;
  return more > 0 ? `${first} +${more} more` : first;
}

export function splittersForVendor(
  splitters: PdfSplitterInfo[] | undefined,
  vendor: string | null | undefined,
): PdfSplitterInfo[] {
  const wanted = (vendor ?? "").trim().toLowerCase();
  return (splitters ?? []).filter((splitter) => splitter.provider === wanted);
}

export function splitProvidersOf(config: PdfToolsConfig | undefined): string[] {
  if (!config) return [];
  if (config.providers?.length) return config.providers;
  return Array.from(new Set(config.splitters.map((s) => s.provider))).sort();
}

export const FOLDER_NAME_MAX = 100;
const FOLDER_NAME_RE = /^[A-Za-z0-9 _.()-]+$/;

/** The one folder segment Save writes into - the backend's rule, so a name it
 * refuses is never sent. */
export function normalizeSplitFolderName(raw: string): {
  name: string;
  error: string | null;
} {
  const name = raw.split(/\s+/).filter(Boolean).join(" ");
  if (!name) return { name, error: "Enter a folder name." };
  if (name.length > FOLDER_NAME_MAX) {
    return { name, error: `At most ${FOLDER_NAME_MAX} characters.` };
  }
  if (name.includes("/") || name.includes("\\")) {
    return { name, error: "A folder name has no slashes." };
  }
  if (!FOLDER_NAME_RE.test(name)) {
    return { name, error: "Use letters, digits, spaces and _ . ( ) - only." };
  }
  if (name.startsWith(".") || name.endsWith(".")) {
    return { name, error: "A folder name cannot start or end with a dot." };
  }
  return { name, error: null };
}

export function splitSavePreview(dataRoot: string, folderName: string): string {
  const root = dataRoot.endsWith("/") ? dataRoot : `${dataRoot}/`;
  return `${root}${folderName || "<folder>"}/`;
}

export type SplitActionKey =
  | "stop"
  | "resume"
  | "start_over"
  | "save"
  | "download"
  | "view_in_storage"
  | "delete";

export interface SplitAction {
  key: SplitActionKey;
  label: string;
  /** Why it cannot be pressed now; null when it can. */
  disabledReason: string | null;
  danger?: boolean;
}

/** The buttons of a split job page for its state; rare ones go into More. */
export function splitJobActionPlan(
  job: SplitJobState & { has_results_zip: boolean },
  { readOnlyReason, fold }: { readOnlyReason: string | null; fold: boolean },
): { primary: SplitAction[]; more: SplitAction[] } {
  const write = (key: SplitActionKey, label: string, extra?: Partial<SplitAction>): SplitAction => ({
    key,
    label,
    disabledReason: readOnlyReason,
    ...extra,
  });
  const download: SplitAction = {
    key: "download",
    label: "Download ZIP",
    disabledReason: job.has_results_zip ? null : "The results archive is not ready yet.",
  };
  const active = isSplitJobActive(job);
  const saving = isSplitJobSaving(job);
  const busyReason = active
    ? "Stop the job first."
    : saving
      ? "Wait for the save to finish."
      : null;
  const startOver = write("start_over", "Start over", {
    disabledReason: readOnlyReason ?? busyReason,
  });
  const remove = write("delete", "Delete", {
    danger: true,
    disabledReason: readOnlyReason ?? busyReason,
  });

  let primary: SplitAction[];
  let more: SplitAction[];
  if (active) {
    primary = [write("stop", "Stop", { danger: true })];
    more = [remove];
  } else if (job.status === "stopped" || job.status === "failed" || job.status === "canceled") {
    primary = [write("resume", "Resume"), startOver];
    more = [remove];
  } else if (isSplitJobSaved(job)) {
    primary = [{ key: "view_in_storage", label: "View in Data Storage", disabledReason: null }, download];
    more = [startOver, remove];
  } else if (saving) {
    primary = [download];
    more = [startOver, remove];
  } else {
    primary = [write("save", "Save to Data Storage"), download];
    more = [startOver, remove];
  }
  if (fold && primary.length > 1) {
    more = [...primary.slice(1), ...more];
    primary = primary.slice(0, 1);
  }
  return { primary, more };
}

interface ApiError {
  status?: number | string;
  data?: unknown;
}

function errorData(error: unknown): Record<string, unknown> | null {
  const data = (error as ApiError | null)?.data;
  return data && typeof data === "object" ? (data as Record<string, unknown>) : null;
}

/** The 429 of the global split limit, with the jobs that hold the slots. */
export function parseSplitConcurrencyError(error: unknown): {
  message: string;
  limit: number | null;
  activeJobs: PdfActiveJobRef[];
} | null {
  const status = (error as ApiError | null)?.status;
  const data = errorData(error);
  if (status !== 429 || !data || data.code !== "split_limit_reached") return null;
  return {
    message: String(data.error ?? "Too many split jobs are running."),
    limit: typeof data.limit === "number" ? data.limit : null,
    activeJobs: Array.isArray(data.active_jobs) ? (data.active_jobs as PdfActiveJobRef[]) : [],
  };
}

/** The code of a refusal ({code, error|message}), for branching on it. */
export function apiErrorCode(error: unknown): string | null {
  const code = errorData(error)?.code;
  return typeof code === "string" ? code : null;
}

/** The sentence of a refusal: our {error|message}, else DRF's shapes. */
export function apiErrorText(error: unknown, fallback: string): string {
  const data = errorData(error);
  if (!data) return fallback;
  const text = data.error ?? data.message ?? data.detail;
  if (typeof text === "string" && text) return text;
  const fields = Object.entries(data)
    .filter(([key]) => !["code", "limit", "active_jobs", "destination"].includes(key))
    .map(([, value]) => (Array.isArray(value) ? value.join(" ") : String(value)));
  return fields.join(" ") || fallback;
}
